import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { REGEXP_ONLY_DIGITS } from "input-otp";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card.tsx";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form";

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/shadcn/input-otp";
import {afterAuthRedirect, constants} from "@/constants";
import { alertError } from "@/utils/utils.ts";
import { Button } from "@/components/shadcn/button.tsx";

const CODE_LENGTH = 6;

const FormSchema = z.object({
  code: z.string().min(CODE_LENGTH, {
    message: `Your one-time password must be ${CODE_LENGTH} characters.`,
  }),
});

interface OtpFormProps {
  email: string | null;
  onClose: () => void;
}

const CustomOTPSlot = ({ index }: { index: number }) => {
  return <InputOTPSlot className="h-12 w-12 text-2xl" index={index} />;
};

export const OtpForm = ({ email, onClose }: OtpFormProps) => {
  const { t } = useTranslation();

  const [codeGenerated, setCodeGenerated] = useState<boolean>(false);
  const [needResend, setNeedResend] = useState<boolean>(false);
  const [codeNum, setCodeNum] = useState<string>("");

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      code: "",
    },
  });

  const generateOtpCode = async (email: string) => {
    try {
      let resp = await fetch(`${constants.url.API_PREFIX}auth/otp/create?email=${email}`);
      let data = await resp.json();
      if (data.code_num) {
        setCodeNum(data.code_num);
        return
      }
      if (data.error_code) {
        alertError(t(data.error_code));
        if (data.error_code == "err.login_page.otp.user_banned") {
          setNeedResend(true)
        }
      }
    } catch (e) {
    }
  }

  const resendCode = () => {
    form.clearErrors("code");
    form.setValue("code", "")
    generateOtpCode(email);
    setCodeGenerated(true);
    setNeedResend(false);
  };

  useEffect(() => {
    if (!codeGenerated && email) {
      resendCode();
    }
  }, []);

  async function onChange() {
    let values = form.getValues();
    if (values && values.code.length < 6) {
      form.clearErrors("code");
      return
    }

    try {
      let resp = await fetch(
        `${constants.url.API_PREFIX}auth/otp/auth?email=${email}&code=${values.code}`,
      );
      let data = await resp.json();
      if (data.success == true) {
        window.location.href=afterAuthRedirect
        return;
      }

      if (data.error_code) {
        form.setError("code", { message: t(data.error_code) });
        if (data.error_code == "err.login_page.otp.max_attempts_reached") {
          setNeedResend(true);
        }
      } else {
        form.setError("code", { message: data.error });
      }
    } catch (e) {

    }
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-xl">{t("login_page.title")}</CardTitle>
        {/*<CardDescription></CardDescription>*/}
      </CardHeader>
      <CardContent className="flex flex-col items-center px-6">
        <div className="py-6">
          <Form {...form}>
            <form onChange={onChange}>
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("login_page.one_time_password")}{codeNum && ` #${codeNum}`}</FormLabel>
                    {!needResend && (
                      <>
                        <FormControl>
                          <div className="flex justify-center py-2">
                            <InputOTP
                              maxLength={6}
                              pattern={REGEXP_ONLY_DIGITS}
                              autoFocus={true}
                              {...field}
                            >
                              <InputOTPGroup>
                                <CustomOTPSlot index={0} />
                                <CustomOTPSlot index={1} />
                                <CustomOTPSlot index={2} />
                              </InputOTPGroup>
                              <InputOTPSeparator />
                              <InputOTPGroup>
                                <CustomOTPSlot index={3} />
                                <CustomOTPSlot index={4} />
                                <CustomOTPSlot index={5} />
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                        </FormControl>
                        <FormDescription>
                          {t("login_page.otp_description")}&nbsp;<b>{email}</b>
                        </FormDescription>
                      </>
                    )}

                    {needResend && (
                      <Button
                        variant="outline"
                        className="w-full my-2"
                        onClick={resendCode}
                      >
                        {t("login_page.otp.resend_code")}
                      </Button>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <div className="w-full flex items-center justify-between pt-5">
          <span
            className="underline cursor-pointer opacity-40 hover:opacity-100"
            onClick={onClose}
          >
            {t("login_page.otp.back")}
          </span>
          <span className="underline cursor-pointer opacity-40 hover:opacity-100" onClick={resendCode}>
            {t("login_page.otp.resend_code")}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
